// src/services/vendasService.js
import { db } from './firebase';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
  limit as qLimit,
} from 'firebase/firestore';

// ✅ Score Events
import { EVENT_TYPES } from './leadershipEngine/score.config';

/* =====================================================================
   HELPERS
===================================================================== */

// Converte Date/Timestamp-like/string para Date (segura)
function toDateSafe(v) {
  if (!v) return null;

  if (v instanceof Timestamp) return v.toDate();
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;

  if (typeof v === 'object' && typeof v.toDate === 'function') {
    const d = v.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof v === 'string') {
    const s = v.trim();

    // yyyy-mm-dd
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const d = new Date(`${s}T12:00:00`);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    // dd/mm/yyyy
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
      const [dd, mm, yyyy] = s.split('/');
      const d = new Date(`${yyyy}-${mm}-${dd}T12:00:00`);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    const d = new Date(s.includes('T') ? s : `${s}T12:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Converte string/datetime para Timestamp (com tolerância a formatos comuns)
function ts(d) {
  if (!d) return Timestamp.fromDate(new Date());
  if (d instanceof Timestamp) return d;

  // Se vier do Firestore (Timestamp-like)
  if (typeof d === 'object' && typeof d.toDate === 'function') return d;

  const asDate = toDateSafe(d);
  return Timestamp.fromDate(asDate || new Date());
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

// Date -> "YYYY-MM-DD" (pra manter compat/legado e debug)
function toIsoDateString(v) {
  const d = toDateSafe(v);
  if (!d) return '';
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Converte valores de dinheiro de forma segura:
 * - number -> number
 * - "30000" -> 30000
 * - "30.000,00" -> 30000
 * - "R$ 14.500" -> 14500
 */
function parseNumero(v) {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;

  const s = String(v).trim();
  const cleaned = s.replace(/[^\d.,-]/g, '');

  // se tem vírgula, assume pt-BR: "." milhar e "," decimal
  if (cleaned.includes(',')) {
    const normalized = cleaned.replace(/\./g, '').replace(',', '.');
    const n = Number(normalized);
    return Number.isFinite(n) ? n : 0;
  }

  // se não tem vírgula: pode ser "14500" ou "14.500" (milhar)
  const normalized = cleaned.replace(/\./g, '');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

// ✅ helper de score event (não quebra fluxo)
async function registrarEventoScore({ uid, tipo, refId = null }) {
  try {
    if (!uid || !tipo) return;
    await addDoc(collection(db, 'eventos_score'), {
      uid,
      tipo,
      refId: refId || null,
      createdAt: Timestamp.fromDate(new Date()),
    });
  } catch (err) {
    console.warn(
      '[ScoreEvent] Falha ao registrar evento:',
      tipo,
      err?.message || err
    );
  }
}

/* =====================================================================
   1) FUNÇÃO BASE — usada por todos os fluxos
===================================================================== */

async function registrarVendaBase({
  // IMPORTANTE: corretorId deve ser SEMPRE o auth.uid
  corretorId,
  corretorNome,

  imovelId = null,
  negocioId = null,

  clienteNome,
  produtoDescricao,
  valorVenda,
  comissao,
  dataVenda,

  origem, // 'imovel' | 'negocio' | 'manual'
}) {
  if (!corretorId) throw new Error('corretorId obrigatório.');
  if (!origem)
    throw new Error("origem é obrigatória ('imovel' | 'negocio' | 'manual').");

  const dataTs = ts(dataVenda || new Date());

  const payload = {
    // padronização: uid é o DONO (auth.uid)
    uid: corretorId,
    corretorId,
    corretorNome: corretorNome || null,

    imovelId,
    negocioId,

    clienteNome: (clienteNome || '').trim(),
    produtoDescricao: (produtoDescricao || '').trim(),

    valor: parseNumero(valorVenda),
    comissao: parseNumero(comissao),

    // ✅ sempre Timestamp (base de TODAS as queries do dashboard)
    data: dataTs,

    // ✅ legado/debug (opcional, mas ajuda muito)
    // mantém compat caso algum fluxo ainda leia dataVenda
    dataVenda: toIsoDateString(dataTs),

    origem,
    createdAt: Timestamp.fromDate(new Date()),
  };

  const ref = await addDoc(collection(db, 'vendas'), payload);

  // ✅ Evento de Score: venda cadastrada
  await registrarEventoScore({
    uid: corretorId,
    tipo: EVENT_TYPES.VENDA_CADASTRADA,
    refId: ref.id,
  });

  return { id: ref.id, ...payload };
}

/* =====================================================================
   2) UTILITÁRIOS DE CÁLCULO (Dashboard)
===================================================================== */

export function calcularTotaisDashboard(vendas) {
  return vendas.reduce(
    (acc, v) => {
      acc.vgvTotal += Number(v.valor || 0);
      acc.faturamentoTotal += Number(v.comissao || 0);
      return acc;
    },
    { vgvTotal: 0, faturamentoTotal: 0 }
  );
}

/* =====================================================================
   3) REGISTRAR VENDA A PARTIR DE UM IMÓVEL
===================================================================== */

export async function registrarVendaDesdeImovel(imovel, dadosVenda) {
  if (!imovel) throw new Error('Imóvel obrigatório.');

  // ⚠️ Ideal: corretorId = auth.uid
  // Mantendo compatibilidade: pega dono do imóvel
  const corretorId =
    imovel.ownerId ||
    imovel.usuarioId ||
    imovel.criadoPor?.uid ||
    imovel.corretorId;

  const corretorNome =
    imovel.ownerNome ||
    imovel.criadoPor?.nome ||
    imovel.corretorNome ||
    'Corretor';

  const produtoDescricao =
    dadosVenda?.produtoDescricao ||
    imovel.titulo ||
    `${imovel.tipo || 'Imóvel'} - ${imovel.bairro || ''} ${imovel.cidade || ''}`.trim();

  return registrarVendaBase({
    corretorId,
    corretorNome,
    imovelId: imovel.id,
    negocioId: null,

    clienteNome: dadosVenda?.clienteNome,
    produtoDescricao,

    valorVenda: dadosVenda?.valorVenda,
    comissao: dadosVenda?.comissao,
    dataVenda: dadosVenda?.dataVenda,

    origem: 'imovel',
  });
}

/* =====================================================================
   4) REGISTRAR VENDA A PARTIR DE UM NEGÓCIO DA CENTRAL
===================================================================== */

export async function registrarVendaDesdeNegocio(negocio, dadosVenda) {
  if (!negocio) throw new Error('Negócio obrigatório.');

  const produtoDescricao =
    dadosVenda?.produtoDescricao ||
    `${negocio.tipoImovel || 'Imóvel'} ${negocio.bairrosDesejados?.[0] || ''} (${negocio.cidadePrincipal || ''})`.trim();

  return registrarVendaBase({
    corretorId: negocio.corretorId,
    corretorNome: negocio.corretorNome,

    negocioId: negocio.id, // ✅ sempre o id do negócio
    imovelId: dadosVenda?.imovelId || null,

    clienteNome:
      dadosVenda?.clienteNome || negocio.clientePrimeiroNome || 'Cliente',

    produtoDescricao,
    valorVenda: dadosVenda?.valorVenda,
    comissao: dadosVenda?.comissao,
    dataVenda: dadosVenda?.dataVenda,

    origem: 'negocio',
  });
}

/* =====================================================================
   5) Função antiga — compatibilidade com versões velhas
===================================================================== */

export async function criarVenda(uid, dados) {
  return registrarVendaBase({
    corretorId: uid,
    corretorNome: dados?.corretorNome || 'Corretor',

    imovelId: dados?.imovelId || null,
    negocioId: dados?.negocioId || null,

    clienteNome: dados?.clienteNome,

    // ✅ aceita tanto produtoTipo quanto produtoDescricao
    produtoDescricao: dados?.produtoDescricao || dados?.produtoTipo,

    valorVenda: dados?.valor,
    comissao: dados?.comissao,

    // ✅ aceita data (string/date/timestamp)
    dataVenda: dados?.data || dados?.dataVenda,

    origem: dados?.origem || 'manual',
  });
}

/* =====================================================================
   6) Atualizar venda (SUPORTA EDIÇÃO)
===================================================================== */

export async function atualizarVenda(id, patch = {}) {
  const ref = doc(db, 'vendas', id);
  const updateData = { ...patch };

  // ✅ Compat UI (TabelaVendas) — se vier "nome/produto"
  if (updateData.nome !== undefined && updateData.clienteNome === undefined) {
    updateData.clienteNome = updateData.nome;
    delete updateData.nome;
  }
  if (
    updateData.produto !== undefined &&
    updateData.produtoDescricao === undefined &&
    updateData.produtoTipo === undefined
  ) {
    updateData.produtoDescricao = updateData.produto;
    delete updateData.produto;
  }

  // ✅ Compat: valorVenda -> valor
  if (updateData.valorVenda !== undefined && updateData.valor === undefined) {
    updateData.valor = updateData.valorVenda;
    delete updateData.valorVenda;
  }

  // números
  if (updateData.valor !== undefined)
    updateData.valor = parseNumero(updateData.valor);
  if (updateData.comissao !== undefined)
    updateData.comissao = parseNumero(updateData.comissao);

  // ✅ data: suporta "dataVenda" ou "data"
  // - sempre mantém "data" Timestamp correto
  // - e grava dataVenda string (legado/debug)
  const hasDataVenda = updateData.dataVenda !== undefined;
  const hasData = updateData.data !== undefined;

  if (hasDataVenda && !hasData) {
    updateData.data = ts(updateData.dataVenda);
  } else if (hasData) {
    updateData.data = ts(updateData.data);
  }

  if (hasDataVenda || hasData) {
    updateData.dataVenda = toIsoDateString(updateData.data);
  }

  // mapeamento legacy
  if (updateData.produtoTipo !== undefined) {
    updateData.produtoDescricao = updateData.produtoTipo;
    delete updateData.produtoTipo;
  }

  await updateDoc(ref, updateData);
}

/* =====================================================================
   7) Lista vendas do corretor
===================================================================== */

export async function listarVendas(
  uid,
  { de, ate, order = 'desc', limit = 100 } = {}
) {
  const clauses = [where('uid', '==', uid)];
  if (de) clauses.push(where('data', '>=', ts(de)));
  if (ate) clauses.push(where('data', '<=', ts(ate)));

  const direction = order === 'asc' ? 'asc' : 'desc';

  const q = query(
    collection(db, 'vendas'),
    ...clauses,
    orderBy('data', direction),
    qLimit(limit)
  );

  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listarVendasRecentes(uid, { limit = 50 } = {}) {
  const q = query(
    collection(db, 'vendas'),
    where('uid', '==', uid),
    orderBy('data', 'desc'),
    qLimit(limit)
  );

  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/* =====================================================================
   8) Stream (tempo real)
===================================================================== */

export function streamVendas(uid, callback, options = {}) {
  const { de, ate, order = 'desc', limit = 100 } = options;

  const clauses = [where('uid', '==', uid)];
  if (de) clauses.push(where('data', '>=', ts(de)));
  if (ate) clauses.push(where('data', '<=', ts(ate)));

  const direction = order === 'asc' ? 'asc' : 'desc';

  const q = query(
    collection(db, 'vendas'),
    ...clauses,
    orderBy('data', direction),
    qLimit(limit)
  );

  // ✅ Coloca onError pra não quebrar o app em caso de regra/índice
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    (err) => {
      console.error('[streamVendas] Firestore snapshot error:', err);
      callback([]); // fallback seguro
    }
  );
}
